import React, { useLayoutEffect, useRef } from 'react';
import * as StatsActions from '../../actions/StatsActions'

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { StringHelper } from '../../helpers/StringHelper';

am4core.useTheme(am4themes_dark);
am4core.useTheme(am4themes_animated);

export interface IPerYearAndMonthSortedBarChartProps {
    id: string;
    colors: am4core.Color[];
    data: Array<StatsActions.IPerYearAndMonthRankingsType>
  }

const PerYearAndMonthSortedBarChart: React.FC<IPerYearAndMonthSortedBarChartProps> = (props: IPerYearAndMonthSortedBarChartProps) => {
    const chart = useRef();

    useLayoutEffect(() => {
        let c = am4core.create(`${props.id}`, am4charts.XYChart);

        chart.current = c as any;
    
        c.padding(40, 40, 40, 40);

        // Prep the data - we need all charts in each year that are in the list so we can update data in place
        // for animations across years. Simply replacing the data causes flash updates and the array size must
        // stay constant across years for the data to animate.
        let minYear = "9999.12";
        let maxYear = "0000.01";
        for (let i = 0; i < props.data.length; i++) {
            let yearMonth = `${props.data[i].year}.${props.data[i].month.toLocaleString('en', {minimumIntegerDigits: 2})}`;
            minYear = yearMonth.localeCompare(minYear) < 0 ? yearMonth : minYear;
            maxYear = yearMonth.localeCompare(maxYear) > 0 ? yearMonth : maxYear;
        }

        // Get all the unique notechart names across all years and months.
        let uniqueNotechartNames: string[] = [];
        for (let i = 0; i < props.data.length; i++) {
            let notechartNames = props.data[i].rankings.map((r) => r.name);

            for (let j = 0; j < notechartNames.length; j++) {
                if (!uniqueNotechartNames.includes(notechartNames[j])) {
                    uniqueNotechartNames.push(notechartNames[j]);
                }
            }
        }

        // Prepare the data for easy consumption by the chart.
        let chartData: any = {};
        let chartDates: string[] = [];
        for (let i = 0; i < props.data.length; i++) {
            // Make a copy of the unique names chart so we can pop off the notechart names that exist in the input data (and have values).
            let missingNotechartNames = [...uniqueNotechartNames];
            let currentYearAndMonth = `${props.data[i].year}.${props.data[i].month.toLocaleString('en', {minimumIntegerDigits: 2})}`;
            chartDates.push(currentYearAndMonth);
            chartData[currentYearAndMonth] = [];
            for (let j = 0; j < props.data[i].rankings.length; j++) {
                let ranking = props.data[i].rankings[j];
                chartData[currentYearAndMonth].push({
                    name: StringHelper.escapeAmchartsString(ranking.name),
                    count: ranking.count,
                });

                // Remove the notechart name we just processed from the unique list.
                missingNotechartNames.splice(missingNotechartNames.indexOf(ranking.name), 1);
            }

            // Now that we've populated all the valid data, add 0 play count entries to the remaining notecharts in other years.
            for (let j = 0; j < missingNotechartNames.length; j++) {
                chartData[currentYearAndMonth].push({
                    name: StringHelper.escapeAmchartsString(missingNotechartNames[j]),
                    count: 0,
                });
            }

            // Now that all the the data is in, sort it so all entries line up across years for the chart data.
            chartData[currentYearAndMonth].sort((a: any, b: any) => a.name.localeCompare(b.name));

            // Reverse the chart dates since they're in descending order (and we want them in ascending).
            chartDates.sort((a: string, b: string) => a.localeCompare(b))
        }

        let currentChartData = chartData[minYear];
        if (currentChartData !== undefined) {
            c.data = JSON.parse(JSON.stringify(currentChartData)); 
        }

        let categoryAxis = c.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.dataFields.category = "name";
        categoryAxis.renderer.minGridDistance = 1;
        categoryAxis.renderer.inversed = true;
        categoryAxis.renderer.grid.template.disabled = true;
        c.zoomOutButton.disabled = true;

        let zoomEnd = 1;
        if (props.data.length > 0) {
            zoomEnd = props.data[0].rankings.length / uniqueNotechartNames.length;
        }

        categoryAxis.zoom({ start: 0, end: zoomEnd });

        let valueAxis = c.xAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;

        let series = c.series.push(new am4charts.ColumnSeries());
        series.dataFields.categoryY = "name";
        series.dataFields.valueX = "count";
        series.tooltipText = "{valueX.value}"
        series.columns.template.strokeOpacity = 0;
        series.columns.template.column.cornerRadiusBottomRight = 5;
        series.columns.template.column.cornerRadiusTopRight = 5;

        let labelBullet = series.bullets.push(new am4charts.LabelBullet())
        labelBullet.label.horizontalCenter = "right";
        labelBullet.label.dx = -10;
        labelBullet.label.textAlign = "end";
        labelBullet.label.text = "{values.valueX.workingValue.formatNumber('#.')}";

        // as by default columns of the same series are of the same color, we add adapter which takes colors from chart.colors color set
        series.columns.template.adapter.add("fill", function(fill, target: any){
            return props.colors[target.dataItem.index % props.colors.length]; // c.colors.getIndex(target.dataItem.index);
        });

        let label = c.plotContainer.createChild(am4core.Label)
        label.fontSize = 60;
        label.fillOpacity = 0.4;
        label.x = am4core.percent(97);
        label.y = am4core.percent(95);
        label.horizontalCenter = "right";
        label.verticalCenter = "middle";
        label.zIndex = 1000;
        label.text = maxYear.toString();

        let sliderContainer = c.bottomAxesContainer.createChild(am4core.Container);
        sliderContainer.width = am4core.percent(100);
        sliderContainer.layout = "horizontal";

        let slider = sliderContainer.createChild(am4core.Slider);
        slider.valign = "middle";
        slider.margin(0, 0, 0, 0);
        slider.marginLeft = 30;
        slider.height = 15;
        slider.start = 1;

        let previousYearMonth = minYear;
        slider.events.on("rangechanged", function() {
            if (chartDates.length == 0) {
                label.text = "Loading...";
                return;
            }

            let index = Math.round(slider.start * (chartDates.length - 1));
            let yearMonth = chartDates[index]

            if (previousYearMonth == yearMonth) {
                return;
            }

            previousYearMonth = yearMonth;

            let newData = chartData[yearMonth];
            for (var i = 0; i < c.data.length; i++) {
                c.data[i].name = newData[i].name;
                c.data[i].count = newData[i].count;
            }
          
            c.invalidateRawData();
          
            label.text = yearMonth.toString();
          })

        categoryAxis.sortBySeries = series;
    
        return () => {
            c.dispose();
        };
      }, [props]);
    
    return (
        <div className="PerYearAndMonthSortedBarChat" id={props.id} style={{ width: "100%", height: "500px" }}></div>
    );
  }
  
  export default PerYearAndMonthSortedBarChart;